<script>
import swal from 'sweetalert';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { addHours, isAfter, format, parse } from 'date-fns';

import { getDealerAgreement, updateDealerAgreement } from 'helpers/api';

import DealerAgreementFormField from './dealer-agreement-form-field.vue';
import DealerAgreementFormFieldCheckbox from './dealer-agreement-form-field-checkbox.vue';
import DealerAgreementFormFieldRadio from './dealer-agreement-form-field-radio.vue';

// Setup:
// Ensure global.js has reference to this file
// Dealer Agreement Form - Enhanced Styles for Adobe XD fidelity

// Example Usage:
// <section id="dealer-agreement">
//     <!-- Vue will mount the dealer-agreement.vue component here -->
// </section>

const dateFormat = 'M/D/YYYY hh:mm:ss a';

export default {
    components: {
        DealerAgreementFormField,
        DealerAgreementFormFieldCheckbox,
        DealerAgreementFormFieldRadio,
    },

    mounted() {
        this.loadDealerAgreement();
    },

    data: function () {
        return {
            isPromptVisible: false,
            isVisible: false,
            isProcessing: false,
            errorMessages: [],

            options: {
                ownershipTypeOptions: [
                    { id: 1, label: 'Sole' },
                    { id: 2, label: 'Partnership' },
                    { id: 3, label: 'Corporation' },
                    { id: 4, label: 'LLC' },
                ],
            },

            contract: {
                id: 0,
                customerExtid: '',
                legalEntityName: null,
                ownerOfficer: null,
                contractId: null,
                dealerContractId: null,

                disableWebsiteContractAcknowledgments: null,
                lastWebAcknowledgement: null,
                dateCurrent: null,

                bsCommercialEngines: false,
                bsResidentialEngines: false,
                bsIncludeInDealerLocator: false,
                bsServiceBrandNotSold: false,
                bsEngineDealerSegment: false,

                companyName: '',
                dbaName: '',

                ownerName: '',
                phone: '',
                email: '',
                url: '',

                shippingAddressLine1: '',
                shippingCity: '',
                shippingState: '',
                shippingZip: '',

                billingAddressLine1: '',
                billingCity: '',
                billingState: '',
                billingZip: '',

                federalTaxId: '',
                shopLaborRate: null,

                ownershipTypeId: '',
                pdf: null,
            },
        };
    },

    methods: {
        showPrompt: function () {
            this.isPromptVisible = true;
        },

        hidePrompt: function () {
            this.isPromptVisible = false;

            this.showModal();
        },

        showModal: function () {
            this.isVisible = true;
        },

        hideModal: function () {
            this.hidePrompt();

            this.isVisible = false;
        },

        skipModal: function () {
            const now = Date.now();
            const expiresAt = format(addHours(now, 12), dateFormat);

            localStorage.setItem('dealerAgreementExpiresAt', expiresAt);

            this.hideModal();
        },

        exportToPdf: async function () {
            try {
                const modalContent = this.$refs.modalContent;

                // Recreate the full ancestor chain so all CSS selectors match:
                //   .dealer-agreement .modal-mask .modal-wrapper .modal-container
                // Inline styles neutralize the overlay/scroll layout without affecting inner styles.
                const offScreenRoot = document.createElement('div');

                offScreenRoot.className = 'dealer-agreement';

                Object.assign(offScreenRoot.style, {
                    position: 'fixed',
                    top: '0',
                    left: '-99999px',
                    width: modalContent.offsetWidth + 'px',
                });

                const modalMask = document.createElement('div');

                modalMask.className = 'modal-mask pdf';

                Object.assign(modalMask.style, {
                    position: 'static',
                    display: 'block',
                    width: '100%',
                    height: 'auto',
                    background: 'none',
                });

                const modalWrapper = document.createElement('div');

                modalWrapper.className = 'modal-wrapper';

                Object.assign(modalWrapper.style, {
                    display: 'block',
                    width: '100%',
                });

                const clone = modalContent.cloneNode(true);

                Object.assign(clone.style, {
                    width: '100%',
                    height: 'auto',
                    maxHeight: 'none',
                    overflow: 'visible',
                });

                modalWrapper.appendChild(clone);
                modalMask.appendChild(modalWrapper);
                offScreenRoot.appendChild(modalMask);

                document.body.appendChild(offScreenRoot);

                const canvas = await html2canvas(clone, {
                    scale: 1,
                    useCORS: true,
                    backgroundColor: '#f0f0f0',
                    scrollY: 0,
                    width: clone.offsetWidth,
                    height: clone.scrollHeight,
                    windowWidth: clone.offsetWidth,
                    windowHeight: clone.scrollHeight,
                });

                document.body.removeChild(offScreenRoot);

                const scaleOfPdf = 0.85;
                const imgWidth = canvas.width * scaleOfPdf;
                const imgHeight = canvas.height * scaleOfPdf;

                const pdf = new jsPDF({
                    orientation: imgWidth > imgHeight ? 'l' : 'p',
                    unit: 'px',
                    format: [imgWidth, imgHeight],
                    hotfixes: ['px_scaling'],
                    compress: true,
                });

                const imgData = canvas.toDataURL('image/jpeg');

                pdf.addImage(imgData, 'JPEG', 0, 0, imgWidth, imgHeight);

                const pdfBlob = pdf.output('datauristring');

                if (window.location.hostname === 'localhost') {
                    // Save to local browser (used for testing only)
                    pdf.save('briggs-dealer-agreement.pdf');
                }

                return pdfBlob;
            } catch (error) {
                console.error('Error generating PDF:', error);

                this.errorMessages.push('Failed to generate PDF. Please try again later.');
            }

            return null;
        },

        loadDealerAgreement: async function () {
            try {
                const response = await getDealerAgreement();

                const updatedContract = { ...this.contract, ...response };

                this.contract = updatedContract;

                // If this customer hasn't acknowledged the agreement on the web before, show the modal
                const expiresAt = parse(localStorage.getItem('dealerAgreementExpiresAt'), { format: dateFormat });
                const isSkipped = isAfter(expiresAt, Date.now());

                if (
                    this.contract != null &&
                    this.contract.lastWebAcknowledgement == null &&
                    this.contract.disableWebsiteContractAcknowledgments !== 'T' &&
                    !isSkipped
                ) {
                    this.showPrompt();
                }
            } catch (error) {
                console.error('Error loading dealer agreement:', error);

                this.errorMessages.push('Failed to load dealer agreement. Please try again later.');
            }
        },

        updateDealerAgreement: async function () {
            try {
                this.isProcessing = true;

                const pdf = await this.exportToPdf();
                if (pdf != null) {
                    const result = await updateDealerAgreement(this.contract, pdf);
                    if (result) {
                        swal(
                            'Thank you!',
                            `Your acknowledgement and acceptance of the 2026 Briggs & Stratton® Dealer Agreement and PD Terms & Conditions has been successfully submitted. Once we receive and review your submission, we will send a confirmation email along with a copy of the dealer agreement to ${this.contract.email}.`,
                            'success',
                        );

                        this.skipModal();
                    }
                } else {
                    this.errorMessages.push('PDF generation failed, so the agreement was not updated. Please try again later.');
                }
            } catch (error) {
                console.error('Error updating dealer agreement:', error);

                this.errorMessages.push('Failed to update dealer agreement. Please try again later.');
            } finally {
                this.isProcessing = false;
            }
        },
    },
};
</script>

<template>
    <section class="dealer-agreement" ref="document">
        <transition name="modal" v-if="isPromptVisible">
            <div class="modal-mask prompt" data-html2canvas-ignore="true">
                <div class="modal-wrapper" id="dealer-agreement-container">
                    <div class="modal-container">
                        <div class="modal-body">
                            <form>
                                <section>
                                    <div class="section-fields">
                                        <img src="/assets/images/logos/dealer-agreement-briggs-logo.png" />

                                        <div>
                                            <div>
                                                <strong>Congratulations on achieving {{ contract.bsEngineDealerSegment }} status for 2026!</strong>
                                            </div>

                                            <div>
                                                Your partnership and dedication to the Briggs & Stratton® brand help drive shared success. As part of our dealer
                                                program, you gain exclusive access to genuine Briggs & Stratton® and Vanguard® parts, engines, and benefits that
                                                power growth and profitability. We're excited to partner with you for another successful year -- click Continue
                                                to view your 2026 status and update your dealer locator preferences.
                                            </div>
                                        </div>
                                    </div>
                                </section>
                            </form>
                        </div>

                        <div class="modal-footer">
                            <slot name="footer">
                                <button class="modal-submit-button" @click="hidePrompt">Continue</button>
                            </slot>
                        </div>
                    </div>
                </div>
            </div>
        </transition>

        <transition name="modal" v-if="isVisible">
            <div class="modal-mask" ref="modalMask">
                <div class="modal-wrapper" id="dealer-agreement-container">
                    <div class="modal-container" ref="modalContent">
                        <div class="modal-header">
                            <slot name="header">
                                <img src="/assets/images/logos/dealer-agreement-briggs-logo.png" />

                                <h3>BRIGGS & STRATTON® ENGINE DEALER AGREEMENT</h3>
                            </slot>
                        </div>

                        <div class="modal-subheader">
                            <p>
                                Briggs & Stratton® has made changes to the levels, benefits, and requirements of its Dealer Sales & Service Programs, effective
                                April 1, 2026.
                            </p>

                            <p>
                                Dealers may qualify for one of three exclusive Briggs & Stratton® dealer programs based on prior years' participation. Each
                                program establishes a valued service partnership, enabling access to genuine Briggs & Stratton® and Vanguard® parts, engines,
                                and accessories—along with warranty reimbursement for approved service work.
                            </p>

                            <p>Please review and acknowledge the information below.</p>
                        </div>

                        <div class="modal-body">
                            <slot name="body">
                                <form>
                                    <section>
                                        <div class="section-title">Primary Contact Information</div>

                                        <div class="section-fields">
                                            <dealer-agreement-form-field
                                                v-model="contract.companyName"
                                                title="Business Name (DEALER)"
                                                fieldName="companyName"
                                            />

                                            <dealer-agreement-form-field v-model="contract.legalEntityName" title="DBA Name" fieldName="legalEntityName" />
                                        </div>

                                        <div class="section-fields">
                                            <dealer-agreement-form-field v-model="contract.ownerOfficer" title="Owner Name" fieldName="ownerOfficer" />

                                            <dealer-agreement-form-field v-model="contract.phone" title="Phone No." fieldName="phone" />
                                        </div>

                                        <div class="section-fields">
                                            <dealer-agreement-form-field v-model="contract.email" title="Primary Email" fieldName="email" />

                                            <dealer-agreement-form-field v-model="contract.url" title="Website" fieldName="url" />
                                        </div>
                                    </section>

                                    <section>
                                        <div class="section-title">Physical Address</div>

                                        <div class="section-fields">
                                            <dealer-agreement-form-field
                                                v-model="contract.shippingAddressLine1"
                                                title="Address"
                                                fieldName="shippingAddressLine1"
                                                fullwidth
                                            />
                                        </div>

                                        <div class="section-fields">
                                            <dealer-agreement-form-field v-model="contract.shippingCity" title="City" fieldName="shippingCity" />

                                            <dealer-agreement-form-field v-model="contract.shippingState" title="State" fieldName="shippingState" />

                                            <dealer-agreement-form-field v-model="contract.shippingZip" title="Zip Code" fieldName="shippingZip" />
                                        </div>
                                    </section>

                                    <section>
                                        <div class="section-title">Billing Address</div>

                                        <div class="section-fields">
                                            <dealer-agreement-form-field
                                                v-model="contract.billingAddressLine1"
                                                title="Address"
                                                fieldName="billingAddressLine1"
                                                fullwidth
                                            />
                                        </div>

                                        <div class="section-fields">
                                            <dealer-agreement-form-field v-model="contract.billingCity" title="City" fieldName="billingCity" />

                                            <dealer-agreement-form-field v-model="contract.billingState" title="State" fieldName="billingState" />

                                            <dealer-agreement-form-field v-model="contract.billingZip" title="Zip Code" fieldName="billingZip" />
                                        </div>
                                    </section>

                                    <section>
                                        <div class="section-title">Ownership Type</div>

                                        <div class="section-fields">
                                            <dealer-agreement-form-field-radio
                                                v-model="contract.ownershipTypeId"
                                                fieldName="ownershipTypeId"
                                                :options="options.ownershipTypeOptions"
                                            />

                                            <dealer-agreement-form-field v-model="contract.federalTaxId" title="Federal Tax ID No." fieldName="federalTaxId" />

                                            <dealer-agreement-form-field
                                                v-model="contract.shopLaborRate"
                                                title="Labor Rate ($)"
                                                fieldName="shopLaborRate"
                                                fieldType="number"
                                            />
                                        </div>

                                        <p>
                                            If changes to the contact, address, or ownership information are needed, please contact administration at
                                            <a href="mailto:contractdepartment@powerdistributors.com">contractdepartment@powerdistributors.com</a>
                                        </p>

                                        <p>
                                            <strong
                                                >You have qualified as a Briggs & Stratton® {{ contract.bsEngineDealerSegment }} Engine Dealer based on your
                                                prior years' participation.</strong
                                            >

                                            For more information about the various Dealer levels, along with their benefits and requirements, please refer to
                                            the Briggs & Stratton® Sales & Service Program for Dealers linked below.
                                        </p>

                                        <p>
                                            <a
                                                href="https://www.powerdistributors.com/docs/default-source/account-portal-resources/BAS_SalesServiceProgram.pdf"
                                                target="_blank"
                                                >View Briggs & Stratton® Sales & Service Program for Dealers</a
                                            >
                                        </p>
                                    </section>

                                    <section>
                                        <p><strong>Please confirm the options below:</strong></p>

                                        <div class="section-fields">
                                            <h2 class="section-group">Engine Types Serviced</h2>

                                            <dealer-agreement-form-field-checkbox
                                                v-model="contract.bsCommercialEngines"
                                                title="Commercial Engines (Vanguard®)"
                                                fieldName="bsCommercialEngines"
                                            />

                                            <dealer-agreement-form-field-checkbox
                                                v-model="contract.bsResidentialEngines"
                                                title="Residential Engines"
                                                fieldName="bsResidentialEngines"
                                            />
                                        </div>

                                        <div class="section-fields">
                                            <h2 class="section-group">Other Services</h2>

                                            <dealer-agreement-form-field-checkbox
                                                v-model="contract.bsIncludeInDealerLocator"
                                                title="Include on Web-based Dealer Locator"
                                                fieldName="bsIncludeInDealerLocator"
                                            />

                                            <dealer-agreement-form-field-checkbox
                                                v-model="contract.bsServiceBrandNotSold"
                                                title="Provide Service for Brands We Don't Sell"
                                                fieldName="bsServiceBrandNotSold"
                                            />
                                        </div>
                                    </section>
                                </form>

                                <div>
                                    <p>
                                        <a
                                            href="https://www.powerdistributors.com/docs/default-source/account-portal-resources/BAS_DealerApplication.pdf"
                                            target="_blank"
                                            >View Briggs & Stratton® Dealer Agreement</a
                                        >
                                    </p>

                                    <p>
                                        <a
                                            href="https://www.powerdistributors.com/docs/default-source/account-portal-resources/BAS_CustomerTermsConditions.pdf"
                                            target="_blank"
                                            >View PD Terms & Conditions</a
                                        >
                                    </p>

                                    <div>&nbsp;</div>

                                    <p class="italic">
                                        By clicking Agree, you accept the new dealer customer segment for this year based on last year’s performance, as well as
                                        the terms of the Briggs & Stratton® Dealer Agreement, and the PD Terms and Conditions linked above. Please review these
                                        documents carefully before finalizing your application. Once completed, a copy of this agreement will be sent via email
                                        to the designated contract record holder on file.
                                    </p>
                                </div>
                            </slot>

                            <div v-if="errorMessages && errorMessages.length" class="alert alert-errors">
                                <ul>
                                    <li v-for="(message, index) in errorMessages" :key="index">{{ message }}</li>
                                </ul>
                            </div>
                        </div>

                        <div class="modal-footer" ref="modalFooter" data-html2canvas-ignore="true">
                            <slot name="footer">
                                <button class="modal-submit-button" @click="updateDealerAgreement" :disabled="isProcessing">Acknowledge & Submit</button>

                                <button class="modal-skip-button" @click="skipModal" :disabled="isProcessing">Skip For Now</button>

                                <div v-if="isProcessing" class="loading-indicator">
                                    <img src="/assets/pd-loading.gif" alt="" />
                                </div>
                            </slot>
                        </div>
                    </div>
                </div>
            </div>
        </transition>
    </section>
</template>
